import * as React from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Eye, EyeOff, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { RequiredMark } from "./required-mark";

export type InputType = "text" | "password" | "integer" | "float";
export type LabelStyle = "floating" | "top" | "none";

export interface UnifiedInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "type"> {
  inputType?: InputType;
  labelStyle?: LabelStyle;
  label?: string;
  value?: string | number;
  onChange?: (value: string | number | undefined) => void;
  maxDecimals?: number;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  tooltip?: string;
  error?: string;
  wrapperClassName?: string;
  subWrapperClassName?: string;
}

const UnifiedInput = React.forwardRef<HTMLInputElement, UnifiedInputProps>(
  (
    {
      className,
      inputType = "text",
      labelStyle = "floating",
      label,
      value: propValue,
      onChange,
      maxDecimals = 2,
      leftElement,
      rightElement,
      tooltip,
      error,
      wrapperClassName,
      subWrapperClassName,
      disabled,
      placeholder,
      id,
      required,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = React.useState("");
    const [isFocused, setIsFocused] = React.useState(false);
    const { t } = useTranslation("shared");
    const [showPassword, setShowPassword] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Sync internal value with prop value when not focused
    React.useEffect(() => {
      if (!isFocused) {
        if (propValue === undefined || propValue === null) {
          setInternalValue("");
        } else if (inputType === "integer" || inputType === "float") {
          if (isNaN(Number(propValue))) {
            setInternalValue("");
          } else {
            setInternalValue(String(propValue));
          }
        } else {
          setInternalValue(String(propValue));
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propValue, inputType]);

    const validateAndSetValue = (currentInputValue: string) => {
      // For integer: only allow digits and optional leading minus
      if (inputType === "integer") {
        if (currentInputValue !== "" && !/^-?\d*$/.test(currentInputValue)) {
          return false;
        }
      }

      // For float: allow digits, one decimal separator (. or ,), and optional leading minus
      if (inputType === "float") {
        if (currentInputValue !== "" && !/^-?\d*([.,])?\d*$/.test(currentInputValue)) {
          return false;
        }

        // Check max decimals
        if (maxDecimals !== undefined && maxDecimals >= 0) {
          const parts = currentInputValue.split(/[.,]/);
          if (parts[1] && parts[1].length > maxDecimals) {
            return false;
          }
        }
      }

      return true;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let currentInputValue = e.target.value;

      if (!validateAndSetValue(currentInputValue)) {
        return;
      }

      if (inputType === "integer" || inputType === "float") {
        currentInputValue = currentInputValue.replace(/^(-?)0+(\d)/, "$1$2");
      }

      setInternalValue(currentInputValue);

      if (!onChange) return;

      if (currentInputValue === "" || currentInputValue === "-") {
        onChange(undefined);
        return;
      }

      switch (inputType) {
        case "float": {
          const sanitized = currentInputValue.replace(",", ".");
          if (sanitized.endsWith(".")) {
            const num = parseFloat(sanitized);
            onChange(isNaN(num) ? undefined : num);
            return;
          }
          const num = parseFloat(sanitized);
          onChange(isNaN(num) ? undefined : num);
          break;
        }
        case "integer": {
          const num = parseInt(currentInputValue, 10);
          onChange(isNaN(num) ? undefined : num);
          break;
        }
        default:
          onChange(currentInputValue);
          break;
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // Set cursor to the end of the input
      setTimeout(() => {
        const input = e.target;
        const length = input.value.length;
        input.setSelectionRange(length, length);
      }, 0);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      props.onBlur?.(e);
    };

    const handleWrapperClick = () => {
      if (!disabled) {
        setIsFocused(true);
        inputRef.current?.focus();
      }
    };

    // Combine refs
    const setRefs = React.useCallback(
      (element: HTMLInputElement | null) => {
        (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      [ref],
    );

    const hasValue = internalValue !== "" && internalValue !== undefined && internalValue !== null;

    const renderInput = () => (
      <Input
        {...props}
        ref={setRefs}
        id={id}
        aria-required={required || undefined}
        type={inputType === "password" && !showPassword ? "password" : "text"}
        inputMode={inputType === "float" ? "decimal" : inputType === "integer" ? "numeric" : "text"}
        disabled={disabled}
        placeholder={labelStyle === "floating" && label ? undefined : placeholder}
        autoComplete={props.autoComplete ?? "off"}
        className={cn(
          "peer h-full w-full border-0 bg-transparent p-0 shadow-none focus:ring-0 focus:outline-none focus-visible:ring-0",
          labelStyle === "floating" && (hasValue || isFocused) && label && "translate-y-1.5 pl-0.5",
          className,
        )}
        value={internalValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onWheel={(e) => {
          if (inputType === "integer" || inputType === "float") {
            e.currentTarget.blur();
          }
        }}
      />
    );

    const passwordToggle = inputType === "password" && (
      <button
        type="button"
        tabIndex={-1}
        aria-label={showPassword ? t("unifiedInput.hidePassword") : t("unifiedInput.showPassword")}
        className="ml-2 flex shrink-0 items-center text-gray-500 hover:text-gray-700 [&_svg]:size-4"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          setShowPassword((prev) => !prev);
        }}
      >
        {showPassword ? <EyeOff /> : <Eye />}
      </button>
    );

    // Top label style - simple layout
    if (labelStyle === "top") {
      return (
        <div className={cn("flex flex-col gap-2", wrapperClassName)}>
          {label && (
            <Label htmlFor={id}>
              {label}
              {required && <RequiredMark />}
            </Label>
          )}
          <div
            className={cn(
              "relative flex h-10 w-full items-center rounded-lg border bg-white px-3 transition-all duration-200",
              {
                "border-blue-600": isFocused,
                "border-red-500": error,
                "border-gray-300": !isFocused && !error,
                "pointer-events-none bg-gray-50": disabled,
                "hover:border-gray-400": !disabled && !isFocused && !error,
              },
            )}
            onClick={handleWrapperClick}
          >
            {leftElement != null && leftElement !== false && (
              <div className="mr-2 flex shrink-0 flex-row items-center text-gray-500 [&_svg]:size-4">
                {leftElement}
              </div>
            )}
            <div className="min-w-0 flex-1">{renderInput()}</div>

            {passwordToggle}

            {rightElement && (
              <div className="ml-2 flex shrink-0 flex-row items-center">{rightElement}</div>
            )}

            {tooltip && (
              <div className="ml-2 self-center">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="size-5 cursor-help text-blue-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-64 text-sm">{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    }

    // Floating label style (default)
    return (
      <div className={cn("flex flex-col gap-2", wrapperClassName)}>
        <div
          className={cn(
            "relative flex w-full items-center rounded-lg border bg-white px-4 transition-all duration-200",
            label ? "h-14" : "h-full",
            {
              "border-blue-600": isFocused,
              "border-red-500": error,
              "border-gray-300": !isFocused && !error,
              "pointer-events-none bg-gray-50": disabled,
              "hover:border-gray-400": !disabled && !isFocused && !error,
            },
            subWrapperClassName,
          )}
          onClick={handleWrapperClick}
        >
          {leftElement != null && leftElement !== false && (
            <div className="mr-2 flex shrink-0 flex-row items-center text-gray-500 [&_svg]:size-4">
              {leftElement}
            </div>
          )}
          <div className="relative flex min-h-10 min-w-0 flex-1 flex-col justify-center">
            {label && (
              <Label
                className={cn(
                  "pointer-events-none absolute left-0 text-sm text-gray-500 transition-all duration-200 ease-in-out",
                  {
                    "-translate-y-3 text-xs": hasValue || isFocused,
                    "translate-y-0 text-sm": !hasValue && !isFocused,
                  },
                )}
                htmlFor={id}
              >
                {label}
                {required && <RequiredMark />}
              </Label>
            )}

            {renderInput()}
          </div>

          {passwordToggle}

          {rightElement && (
            <div className="ml-2 flex shrink-0 flex-row items-center">{rightElement}</div>
          )}

          {tooltip && (
            <div className="ml-2 self-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="size-5 cursor-help text-blue-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-64 text-sm">{tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  },
);

UnifiedInput.displayName = "UnifiedInput";

export { UnifiedInput };
